import { Flex, InputNumber, RadioChangeEvent } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getCompanyDebtListByOwnerMarketIdThunk,
    getInvoiceDebtListThunk,
    getMarketInfoCollectionThunk,
} from "tm-shared/api";
import { Debt, TaskTakeDebt } from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";
import { v7 } from "uuid";

import BaseButton from "@/components/BaseButton";
import FlexContainer from "@/components/FlexContainer";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { WarningModal } from "@/components/Modals/Warning";
import { SVScanButton } from "@/components/SVScanButton";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { UniversalTabs } from "@/components/UniversalTabs";
import { ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { makeCommand, useMakeCommand } from "@/hooks/useRequestQueue";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { setTaskCompleted } from "@/store/slices/work";
import { handleFocus } from "@/utils/dom/handleFocus";

import { ByContragent } from "./ByContragent";
import { ByMarket } from "./ByMarket";
import { DebtSummaryHeader } from "./DebtSummaryHeader";
import DiscardPaymentModal from "./DiscardPaymentModal";
import { ButtonsWrapper, StyledMainContent } from "./Styled";

interface TakeDebtPageProps {
    marketId: string;
    task: TaskTakeDebt;
}

export const TakeDebtPage: FC<TakeDebtPageProps> = ({ marketId, task }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { makeTaskCompleteCommands } = useMakeCommand();

    const { markets } = useAppSelector((state) => state.work);
    const { debts: currentDebts } = useAppSelector((state) => state.work);
    const { offlineMode, debts, debtsByFOP } = useAppSelector(
        (state) => state.offline,
    );
    const { id, role } = useAppSelector((s) => s.user);
    const { activeSchedule } = useAppSelector((s) => s.sv);

    const taskId = task.id;

    const warningModalKey = useMemo(() => v7(), []);
    const [modal, setModal] = useState<null | "warning" | "discharged">(null);
    const [currentTab, setCurrentTab] = useState<string>("market");
    const [discardPaymentType, setDiscardPaymentType] = useState<number>(1);
    const [currentMarketInvoiceIds, setCurrentMarketInvoiceIds] = useState<
        string[]
    >([]);
    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [currentContragentInvoiceIds, setCurrentContragentInvoiceIds] =
        useState<string[]>([]);
    const [debtValue, setDebtValue] = useState<number>(0);

    const debtMarketList: Record<string, Debt> | undefined = offlineMode
        ? debts?.[marketId]
        : currentDebts;

    const debtFOPList = debtsByFOP?.[marketId] || {};
    const isMarket = currentTab === "market";
    const isContragent = currentTab === "contragent";

    const totalDebtSum = useMemo(
        () =>
            Object.values(debtMarketList || {}).reduce(
                (acc, item) => acc + (item.debt || 0),
                0,
            ),
        [debtMarketList],
    );

    const chosenDebtMarketSum = useMemo(
        () =>
            currentMarketInvoiceIds.reduce(
                (acc, item) => acc + (debtMarketList?.[item]?.debt || 0),
                0,
            ),
        [currentMarketInvoiceIds, debtMarketList],
    );

    const flattedContragentList = useMemo(
        () => Object.values(debtFOPList).flat(),
        [debtFOPList],
    );

    const chosenDebtContragentSum = useMemo(
        () =>
            currentContragentInvoiceIds.reduce((acc, consignmentId) => {
                const found = flattedContragentList.find(
                    (i) => i.consignmentId === consignmentId,
                );
                return acc + (found?.debtAmount ?? 0);
            }, 0),
        [currentContragentInvoiceIds, flattedContragentList],
    );

    const currentMarketAmount = useMemo(
        () =>
            currentMarketInvoiceIds.length ? chosenDebtMarketSum : totalDebtSum,
        [currentMarketInvoiceIds, chosenDebtMarketSum, totalDebtSum],
    );

    const currentContragentAmount = useMemo(
        () =>
            currentContragentInvoiceIds.length ? chosenDebtContragentSum : 0,
        [currentContragentInvoiceIds, chosenDebtContragentSum],
    );

    const maxAllowedSum = isMarket
        ? currentMarketAmount
        : currentContragentAmount;

    const taskCompleteLocal = useCallback(() => {
        dispatch(setTaskCompleted({ marketId, taskId }));

        navigate(ROUTES.WorkPlan, { replace: true });
    }, [dispatch, marketId, navigate, taskId]);

    const [handleSendPayment, sendPaymentLoading] = useAsyncAction(async () => {
        const normalizedDebtValue = +debtValue.toFixed(2);

        if (normalizedDebtValue > +maxAllowedSum.toFixed(2)) {
            setModal("warning");
            return;
        }

        const consignmentsIds = isMarket
            ? currentMarketInvoiceIds
            : currentContragentInvoiceIds;

        const paymentParams = {
            marketId,
            consignmentsIds: consignmentsIds.length
                ? consignmentsIds
                : undefined,
            amount: normalizedDebtValue,
            isFopPay: isContragent,
            agentId: isManagerRole(role) ? activeSchedule : id,
        };

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: { result: true, ...paymentParams },
            },
            marketId,
        });

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: {
                    marketId,
                    taskId,
                },
                options: {
                    todayOnly: true,
                },
                commands: [
                    makeCommand({
                        type: "postInvoiceMakePayment",
                        title: `Створення рахунку на суму ${formatUAHStandard(
                            paymentParams.amount,
                        )}`,
                        params: paymentParams,
                    }),

                    ...taskCompleteCommands,

                    makeCommand({
                        type: "getInvoiceDebtListThunk",
                        title: "Оновлення боргів торгової точки",
                        params: { marketId },
                    }),
                    makeCommand({
                        type: "getMarketInfoCollectionWorkThunk",
                        title: "Оновлення інформації про торгову точку",
                        params: { marketIds: [marketId] },
                    }),
                ],
            }),
        );

        taskCompleteLocal();
    });

    const [handleDiscardPayment, discardPaymentLoading] = useAsyncAction(
        async () => {
            const comment =
                discardPaymentType === 1
                    ? "Відсутні кошти у ТТ на даний момент."
                    : "Зроблено безготівкове перерахування.";

            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload: { result: false, comment },
                },
                marketId,
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        marketId,
                        taskId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: taskCompleteCommands,
                }),
            );

            taskCompleteLocal();
        },
    );

    useEffect(() => {
        if (!offlineMode) {
            dispatch(getMarketInfoCollectionThunk({ marketIds: [marketId] }));
            dispatch(getInvoiceDebtListThunk({ marketId }));
            dispatch(getCompanyDebtListByOwnerMarketIdThunk({ marketId }));
        }
    }, [dispatch, marketId, offlineMode]);

    const isSending = sendPaymentLoading || discardPaymentLoading;

    return (
        <>
            {isSending && <Loading />}

            <Meta title="Забрати борг" />
            <Header
                title="Борг"
                back
                rightActions={
                    <SVScanButton marketId={marketId} taskId={taskId} />
                }
            />

            <MainContainer>
                <StyledMainContent>
                    <Flex vertical gap={12}>
                        <FlexContainer
                            alignItems="center"
                            justifyContent="center"
                        >
                            <UniversalTabs
                                value={currentTab}
                                onChange={setCurrentTab}
                                tabs={[
                                    { value: "market", label: "По магазину" },
                                    // {
                                    //     value: "contragent",
                                    //     label: "По контрагенту",
                                    // },
                                ]}
                                maxWidth="280px"
                            />
                        </FlexContainer>

                        <DebtSummaryHeader
                            totalAmount={totalDebtSum}
                            invoicesAmount={
                                isMarket
                                    ? chosenDebtMarketSum
                                    : chosenDebtContragentSum
                            }
                        />

                        <PrimaryText fontSize={16} fontWeight="500">
                            {markets?.[marketId]?.name}
                        </PrimaryText>

                        <Flex vertical gap={8}>
                            <SecondaryText color="disabled">
                                Введіть суму грошей, яку бажаєте забрати
                            </SecondaryText>

                            <InputNumber
                                placeholder="0000.0"
                                size="large"
                                onFocus={handleFocus}
                                value={debtValue}
                                parser={(value) =>
                                    +(value || "")
                                        .replace(",", ".")
                                        .replace(/[^0-9.]/g, "")
                                }
                                inputMode="decimal"
                                onChange={(value) =>
                                    setDebtValue(Number(value) || 0)
                                }
                            />
                        </Flex>
                    </Flex>

                    {isMarket && (
                        <ByMarket
                            currentMarketInvoiceIds={currentMarketInvoiceIds}
                            setCurrentMarketInvoiceIds={
                                setCurrentMarketInvoiceIds
                            }
                            loading={isSending}
                        />
                    )}

                    {isContragent && (
                        <ByContragent
                            selectedDates={selectedDates}
                            setSelectedDates={setSelectedDates}
                            selectedContragentInvoiceIds={
                                currentContragentInvoiceIds
                            }
                            setSelectedContragentInvoiceIds={
                                setCurrentContragentInvoiceIds
                            }
                            loading={isSending}
                        />
                    )}
                </StyledMainContent>

                <ButtonsWrapper>
                    {debtValue > 0 ? (
                        <BaseButton
                            onClick={handleSendPayment}
                            disabled={
                                isContragent &&
                                currentContragentInvoiceIds.length === 0
                            }
                            loading={sendPaymentLoading}
                            type="primary"
                        >
                            Вивести кошти
                        </BaseButton>
                    ) : (
                        <BaseButton
                            type="primary"
                            onClick={() => setModal("discharged")}
                            loading={isSending}
                        >
                            Немає можливості оплати
                        </BaseButton>
                    )}
                </ButtonsWrapper>
            </MainContainer>

            <WarningModal
                modalKey={warningModalKey}
                width="92%"
                isModalOpened={modal === "warning"}
                cancel={() => setModal(null)}
                title="Увага!"
            >
                <Flex vertical gap={20}>
                    <PrimaryText>
                        Бажана сума, яку ви хочете забрати:{" "}
                        <strong>{formatUAHStandard(debtValue)}</strong>
                    </PrimaryText>

                    <PrimaryText>
                        Максимально допустима сума:{" "}
                        <strong>{formatUAHStandard(maxAllowedSum)}</strong>
                    </PrimaryText>
                </Flex>
            </WarningModal>

            <DiscardPaymentModal
                isModalOpened={modal === "discharged"}
                handleCloseModal={() => setModal(null)}
                discardPaymentType={discardPaymentType}
                onDiscardPaymentTypeChange={(e: RadioChangeEvent) =>
                    setDiscardPaymentType(e.target.value)
                }
                handleDiscardPayment={handleDiscardPayment}
                loading={discardPaymentLoading}
            />
        </>
    );
};
